import * as React from "react";

/**
 * The co-pilot chat on the product band, played rather than pictured.
 *
 * <p>It types the request into the composer, moves it up as a sent bubble, then lets each
 * reply land: the read-back, the "what I understood" card, the booked receipt and the
 * quick actions. It starts only when the mock is on screen (one IntersectionObserver,
 * threshold .35), runs exactly once, and under {@code prefers-reduced-motion} it plays
 * nothing at all — the finished exchange is simply shown.
 *
 * <p>⚠️ The one gate is "the panel is on screen". The previous design played the chat
 * after a typed hero headline finished; this page has no hero and no typed headline, so a
 * headline gate would leave the mock empty forever. Do not reintroduce one.
 */

/** The request, in segments so the mentions and the sigil keep their colour while typing. */
const REQUEST: { t: string; c?: "at" | "sig" }[] = [
  { t: "find 30 minutes with " },
  { t: "@priya", c: "at" },
  { t: " and " },
  { t: "@tom", c: "at" },
  { t: " next week, afternoons, and use my " },
  { t: ".{client hours}", c: "sig" },
];
const TOTAL = REQUEST.reduce((a, s) => a + s.t.length, 0);
const STEP_COUNT = 5;

/** The first `upto` characters of the request, segment colours intact. */
function RequestText({ upto }: { upto: number }) {
  let n = 0;
  const parts: React.ReactNode[] = [];
  for (const [i, seg] of REQUEST.entries()) {
    const take = Math.max(0, Math.min(seg.t.length, upto - n));
    if (take > 0) {
      const piece = seg.t.slice(0, take);
      parts.push(seg.c ? <span key={i} className={seg.c}>{piece}</span> : <React.Fragment key={i}>{piece}</React.Fragment>);
    }
    n += seg.t.length;
    if (n >= upto) break;
  }
  return <>{parts}</>;
}

export function SchedaChat() {
  const reduce =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // `playing` arms the hidden state (so with reduced motion nothing is ever hidden),
  // `typed` is how far the composer has got, `sent` moves the request up as a bubble,
  // `revealed` is how many replies have landed.
  const [playing, setPlaying] = React.useState(false);
  const [typed, setTyped] = React.useState(0);
  const [sent, setSent] = React.useState(reduce);
  const [revealed, setRevealed] = React.useState(reduce ? STEP_COUNT : 0);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const started = React.useRef(false);

  React.useEffect(() => {
    if (reduce) return;
    const root = rootRef.current;
    if (!root) return;
    if (!("IntersectionObserver" in window)) {
      // No observer, no play: show the finished exchange rather than an empty pane.
      setSent(true);
      setRevealed(STEP_COUNT);
      return;
    }
    setPlaying(true);

    let cancelled = false;
    const timers: number[] = [];
    const wait = (ms: number) =>
      new Promise<void>((resolve) => {
        timers.push(window.setTimeout(resolve, ms));
      });

    async function play(): Promise<void> {
      await wait(500);
      for (let i = 1; i <= TOTAL; i++) {
        if (cancelled) return;
        setTyped(i);
        // a human types unevenly; a perfectly even crawl reads as a machine
        await wait(i % 7 === 0 ? 54 : 22 + Math.random() * 26);
      }
      await wait(420);
      if (cancelled) return;
      setSent(true); // the request becomes a sent bubble (step 0)
      setRevealed(1);
      await wait(620);
      for (let k = 2; k <= STEP_COUNT; k++) {
        // a short think before the agent's first words, shorter for what follows
        await wait(k === 2 ? 900 : 560);
        if (cancelled) return;
        setRevealed(k);
      }
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started.current) {
            started.current = true;
            io.disconnect();
            void play();
          }
        });
      },
      { threshold: 0.35 },
    );
    io.observe(root);
    return () => {
      cancelled = true;
      io.disconnect();
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [reduce]);

  const typing = playing && !sent && typed > 0;
  const stepClass = (k: number): string => (revealed > k ? "in" : "");

  return (
    <div className={playing ? "mock chat chat-playing" : "mock chat"} ref={rootRef}>
      <div className="chathead">
        <span className="avatar">S</span>
        <div>
          <div className="who">Your agent</div>
          <div className="live">
            <i /> working on one request
          </div>
        </div>
        <span className="pill">New thread</span>
      </div>

      <div className="canvas">
        <div data-step="0" className={`row me ${stepClass(0)}`}>
          <div className="bub me">{sent && <RequestText upto={TOTAL} />}</div>
        </div>

        <div data-step="1" className={`row ${stepClass(1)}`}>
          <span className="avatar" style={{ width: "26px", height: "26px", fontSize: "var(--step--3)" }}>
            S
          </span>
          <div className="bub">
            Here is what I read from that. Both of them are reachable, so I will ask their agents
            directly.
          </div>
        </div>

        <div data-step="2" className={`row ${stepClass(2)}`}>
          <span className="avatar" style={{ width: "26px", height: "26px", background: "transparent" }} />
          <div className="icard" style={{ flex: 1 }}>
            <div className="t">
              <b>◆</b> What I understood
            </div>
            <dl className="kv">
              <dt>Length</dt>
              <dd>30 minutes</dd>
              <dt>Window</dt>
              <dd>Mon 12 to Fri 16 October, 13:00–17:00</dd>
              <dt>With</dt>
              <dd>@priya, @tom</dd>
              <dt>Pattern</dt>
              <dd>Client hours</dd>
            </dl>
            <div className="chips">
              <span className="chip blue">Work meeting</span>
              <span className="chip teal">Google Meet</span>
              <span className="chip">Europe/Copenhagen</span>
            </div>
          </div>
        </div>

        <div data-step="3" className={`row ${stepClass(3)}`}>
          <span className="avatar" style={{ width: "26px", height: "26px", fontSize: "var(--step--3)" }}>
            S
          </span>
          <div className="bub" style={{ flex: 1 }}>
            <div className="receipt">
              <div className="h">
                <i>✓</i> Booked
              </div>
              <div className="when">Tuesday 13 October, 14:30–15:00</div>
              <div className="sub">Invitations sent to 2 people. Added to your calendar.</div>
            </div>
          </div>
        </div>

        <div data-step="4" className={`row ${stepClass(4)}`}>
          <span className="avatar" style={{ width: "26px", height: "26px", background: "transparent" }} />
          <div className="quick">
            <button type="button">Open the meeting</button>
            <button type="button">Move it</button>
            <button type="button">Ask for a longer slot</button>
          </div>
        </div>
      </div>

      <div className="composer">
        <div className={typing ? "box typing" : sent || !playing ? "box" : "box muted"}>
          {!sent && <RequestText upto={typed} />}
          <i className="tcaret" />
        </div>
        <div className="send">Send</div>
      </div>
      <div className="chatfoot">
        <span>
          Conversation <code>4f8a21c3</code>
        </span>
        <span className="tok">● 1,840 tokens today</span>
      </div>
    </div>
  );
}
